import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  cn,
  formatNumber,
  formatPercent,
  timeAgo,
  formatDate,
  formatDuration,
  getStatusColor,
} from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader, CardTitle, CardBody, StatCard } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { LoadingState, EmptyState } from '@/components/ui/States';
import type { Device, Job, Script, Site, Client, JobResult, JobStatus } from '@/types';
import {
  Play,
  RotateCcw,
  RefreshCw,
  Search,
  Filter,
  Terminal,
  ShieldCheck,
  Power,
  Download,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  X,
  Copy,
  Check,
  Ban,
  Activity,
  User,
  Laptop,
  ArrowUpDown,
  MoreVertical,
  SlidersHorizontal,
} from 'lucide-react';

export function Jobs() {
  const navigate = useNavigate();

  // State
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  // Selected job for detail modal
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [jobModalOpen, setJobModalOpen] = useState<boolean>(false);
  const [deviceResultSearch, setDeviceResultSearch] = useState<string>('');
  const [selectedResult, setSelectedResult] = useState<JobResult | null>(null);

  // Cancel job dialog
  const [jobToCancel, setJobToCancel] = useState<Job | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);

  // Copy output state
  const [copiedLog, setCopiedLog] = useState<boolean>(false);

  // Fetch jobs
  const fetchJobsData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await mockApi.getJobs();
      setJobs(data);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchJobsData();
  }, []);

  // Filtered jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      // Status filter
      if (statusFilter !== 'all' && job.status !== statusFilter) {
        return false;
      }

      // Type filter
      if (typeFilter !== 'all' && job.type !== typeFilter) {
        return false;
      }

      // Priority filter
      if (priorityFilter !== 'all' && job.priority !== priorityFilter) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = job.name.toLowerCase().includes(q);
        const matchesInitiated = job.initiatedBy.toLowerCase().includes(q);
        const matchesTarget = job.targetDevices?.some((d) => d.toLowerCase().includes(q));

        if (!matchesName && !matchesInitiated && !matchesTarget) return false;
      }

      return true;
    });
  }, [jobs, statusFilter, typeFilter, priorityFilter, searchQuery]);

  // Statistics summary
  const stats = useMemo(() => {
    const total = jobs.length;
    const running = jobs.filter((j) => j.status === 'running' || j.status === 'pending').length;
    const completed = jobs.filter((j) => j.status === 'completed').length;
    const failed = jobs.filter((j) => j.status === 'failed').length;
    const cancelled = jobs.filter((j) => j.status === 'cancelled').length;

    const successRate = total > 0 ? Math.round((completed / (completed + failed || 1)) * 100) : 100;

    return { total, running, completed, failed, cancelled, successRate };
  }, [jobs]);

  // Handle Cancel Job Action
  const handleConfirmCancel = () => {
    if (!jobToCancel) return;

    setJobs((prevJobs) =>
      prevJobs.map((j) => {
        if (j.id === jobToCancel.id) {
          return {
            ...j,
            status: 'cancelled',
            completedAt: new Date().toISOString(),
          };
        }
        return j;
      })
    );

    if (selectedJob?.id === jobToCancel.id) {
      setSelectedJob((prev) =>
        prev
          ? {
              ...prev,
              status: 'cancelled',
              completedAt: new Date().toISOString(),
            }
          : null
      );
    }

    setJobToCancel(null);
    setCancelDialogOpen(false);
  };

  // Helper function for job type icons & labels
  const getJobTypeMeta = (type: string) => {
    switch (type) {
      case 'script':
        return { label: 'Script', icon: Terminal, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' };
      case 'patch':
        return { label: 'Patch Update', icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' };
      case 'reboot':
        return { label: 'Reboot', icon: RotateCcw, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' };
      case 'shutdown':
        return { label: 'Shutdown', icon: Power, color: 'text-rose-400 bg-rose-500/10 border-rose-500/20' };
      case 'agent_update':
        return { label: 'Agent Update', icon: RefreshCw, color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' };
      case 'inventory':
        return { label: 'Inventory Scan', icon: Activity, color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' };
      case 'file_transfer':
        return { label: 'File Transfer', icon: Download, color: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20' };
      default:
        return { label: type, icon: FileText, color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' };
    }
  };

  // Copy code handler
  const handleCopyOutput = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLog(true);
    setTimeout(() => setCopiedLog(false), 2000);
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader title="Jobs Execution" description="Task execution and script deployment history" />
        <LoadingState rows={8} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-[var(--bg)] min-h-[calc(100vh-4rem)]">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-[var(--text-primary)]">Jobs & Deployments</h1>
            {stats.running > 0 && (
              <Badge variant="online" dot className="animate-pulse">
                {stats.running} Active Running
              </Badge>
            )}
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Monitor, inspect, and manage automated task executions across managed endpoints.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => fetchJobsData(true)} loading={refreshing}>
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Jobs Executed" value={formatNumber(stats.total)} sublabel="Across all managed tenants" />
        <StatCard
          label="Running / Queued"
          value={stats.running}
          status={stats.running > 0 ? 'online' : 'info'}
          sublabel="Currently in progress"
        />
        <StatCard
          label="Success Rate"
          value={`${stats.successRate}%`}
          status={stats.successRate > 90 ? 'online' : 'warning'}
          sublabel={`${stats.completed} succeeded`}
        />
        <StatCard
          label="Failed Executions"
          value={stats.failed}
          status={stats.failed > 0 ? 'critical' : 'online'}
          sublabel={`${stats.cancelled} cancelled jobs`}
        />
      </div>

      {/* Filter & Search Bar */}
      <Card>
        <CardBody className="p-4 space-y-3">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            {/* Search Input */}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
              <input
                type="text"
                placeholder="Search by job name, user, device..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded-md pl-9 pr-8 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)]"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Status Dropdown */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Status:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Statuses</option>
                  <option value="running">Running</option>
                  <option value="pending">Pending</option>
                  <option value="queued">Queued</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Type Dropdown */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Type:</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Types</option>
                  <option value="script">Script</option>
                  <option value="patch">Patch</option>
                  <option value="reboot">Reboot</option>
                  <option value="shutdown">Shutdown</option>
                  <option value="agent_update">Agent Update</option>
                  <option value="inventory">Inventory</option>
                  <option value="file_transfer">File Transfer</option>
                </select>
              </div>

              {/* Priority Dropdown */}
              <div className="flex items-center gap-1.5 bg-[var(--elevated)] border border-[var(--border)] px-2.5 py-1 rounded-md text-xs">
                <span className="text-[var(--text-muted)] font-medium">Priority:</span>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="bg-transparent text-[var(--text-primary)] focus:outline-none capitalize font-medium"
                >
                  <option value="all">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>

              {(statusFilter !== 'all' || typeFilter !== 'all' || priorityFilter !== 'all' || searchQuery) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setPriorityFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-xs text-[var(--text-muted)]"
                >
                  Reset Filters
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Jobs Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--elevated)] text-[var(--text-muted)] font-semibold uppercase text-2xs tracking-wider">
                <th className="px-4 py-3">Job Name & Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Initiated By</th>
                <th className="px-4 py-3">Created / Duration</th>
                <th className="px-4 py-3 text-center">Exit Code</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)] text-[var(--text-primary)]">
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12">
                    <EmptyState
                      icon={<FileText className="h-8 w-8 text-[var(--text-muted)]" />}
                      title="No jobs found"
                      description="No execution jobs match your filter criteria."
                    />
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => {
                  const typeMeta = getJobTypeMeta(job.type);
                  const TypeIcon = typeMeta.icon;
                  const isRunning = job.status === 'running' || job.status === 'pending';

                  // Calculate execution duration
                  let durationSec = 0;
                  if (job.startedAt && job.completedAt) {
                    durationSec = Math.max(
                      1,
                      Math.floor(
                        (new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime()) / 1000
                      )
                    );
                  } else if (job.startedAt) {
                    durationSec = Math.max(
                      1,
                      Math.floor((Date.now() - new Date(job.startedAt).getTime()) / 1000)
                    );
                  }

                  const percentComplete =
                    job.targetCount > 0 ? Math.round((job.completedCount / job.targetCount) * 100) : 0;

                  return (
                    <tr
                      key={job.id}
                      onClick={() => {
                        setSelectedJob(job);
                        setSelectedResult(job.results?.[0] || null);
                        setJobModalOpen(true);
                      }}
                      className="hover:bg-[var(--hover)] transition-colors cursor-pointer group"
                    >
                      {/* Job Name & Type */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              'h-8 w-8 rounded-md border flex items-center justify-center shrink-0',
                              typeMeta.color
                            )}
                          >
                            <TypeIcon className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-semibold text-[var(--text-primary)] group-hover:text-[var(--accent)] transition-colors">
                              {job.name}
                            </div>
                            <div className="text-2xs text-[var(--text-muted)] flex items-center gap-2 mt-0.5">
                              <span>ID: {job.id}</span>
                              <span>•</span>
                              <span className="capitalize">{typeMeta.label}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={job.status} dot={isRunning}>
                          {job.status}
                        </Badge>
                      </td>

                      {/* Priority */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cn(
                            'text-2xs font-medium px-2 py-0.5 rounded capitalize border',
                            job.priority === 'high' && 'text-rose-400 bg-rose-500/10 border-rose-500/20',
                            job.priority === 'normal' && 'text-blue-400 bg-blue-500/10 border-blue-500/20',
                            job.priority === 'low' && 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20'
                          )}
                        >
                          {job.priority}
                        </span>
                      </td>

                      {/* Visual Progress Bar */}
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="space-y-1">
                          <div className="flex justify-between text-2xs text-[var(--text-muted)] font-mono">
                            <span>
                              {job.completedCount}/{job.targetCount} Target
                            </span>
                            <span>{percentComplete}%</span>
                          </div>
                          <div className="h-1.5 w-full bg-[var(--elevated)] rounded-full overflow-hidden border border-[var(--border)]">
                            <div
                              className={cn(
                                'h-full transition-all duration-300',
                                job.status === 'failed'
                                  ? 'bg-[var(--status-critical)]'
                                  : job.status === 'completed'
                                  ? 'bg-[var(--status-online)]'
                                  : 'bg-[var(--accent)]'
                              )}
                              style={{ width: `${percentComplete}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Initiated By */}
                      <td className="px-4 py-3 whitespace-nowrap text-[var(--text-secondary)]">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-[var(--text-muted)]" />
                          <span>{job.initiatedBy}</span>
                        </div>
                      </td>

                      {/* Created Time / Duration */}
                      <td className="px-4 py-3 whitespace-nowrap text-2xs text-[var(--text-muted)]">
                        <div>{timeAgo(job.createdAt)}</div>
                        {job.startedAt && (
                          <div className="text-[var(--text-secondary)] mt-0.5 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(durationSec)}
                          </div>
                        )}
                      </td>

                      {/* Exit Code */}
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {job.exitCode !== null && job.exitCode !== undefined ? (
                          <span
                            className={cn(
                              'font-mono text-2xs font-semibold px-2 py-0.5 rounded border',
                              job.exitCode === 0
                                ? 'text-[var(--status-online)] bg-[var(--status-online)]/10 border-[var(--status-online)]/20'
                                : 'text-[var(--status-critical)] bg-[var(--status-critical)]/10 border-[var(--status-critical)]/20'
                            )}
                          >
                            Exit {job.exitCode}
                          </span>
                        ) : (
                          <span className="text-[var(--text-muted)]">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {isRunning && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                setJobToCancel(job);
                                setCancelDialogOpen(true);
                              }}
                              title="Cancel Running Job"
                            >
                              <Ban className="h-3.5 w-3.5" />
                              Cancel
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedJob(job);
                              setSelectedResult(job.results?.[0] || null);
                              setJobModalOpen(true);
                            }}
                          >
                            Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detailed Job Results Modal */}
      {selectedJob && (
        <Modal
          open={jobModalOpen}
          onClose={() => {
            setJobModalOpen(false);
            setSelectedJob(null);
            setSelectedResult(null);
          }}
          title={`Job Details: ${selectedJob.name}`}
          size="xl"
        >
          <div className="space-y-6">
            {/* Header Job Metadata Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-[var(--elevated)] p-4 rounded-md border border-[var(--border)]">
              <div>
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Job ID</span>
                <span className="font-mono font-medium text-[var(--text-primary)]">{selectedJob.id}</span>
              </div>
              <div>
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Initiated By</span>
                <span className="font-medium text-[var(--text-primary)]">{selectedJob.initiatedBy}</span>
              </div>
              <div>
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Created Time</span>
                <span className="text-[var(--text-secondary)]">{formatDate(selectedJob.createdAt)}</span>
              </div>
              <div>
                <span className="text-2xs text-[var(--text-muted)] uppercase block">Execution Window</span>
                <span className="text-[var(--text-secondary)]">
                  {selectedJob.startedAt ? formatDate(selectedJob.startedAt, 'HH:mm:ss') : 'Not started'} -{' '}
                  {selectedJob.completedAt ? formatDate(selectedJob.completedAt, 'HH:mm:ss') : 'In progress'}
                </span>
              </div>
            </div>

            {/* Target Devices Execution Results Table & Log Split */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
                  Target Device Results ({selectedJob.results?.length || 0})
                </h3>

                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-[var(--text-muted)]" />
                  <input
                    type="text"
                    placeholder="Search device hostname..."
                    value={deviceResultSearch}
                    onChange={(e) => setDeviceResultSearch(e.target.value)}
                    className="w-full bg-[var(--elevated)] border border-[var(--border)] rounded px-8 py-1 text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none"
                  />
                </div>
              </div>

              {/* Devices Grid List */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Left Device List Column */}
                <div className="md:col-span-1 border border-[var(--border)] rounded-md overflow-hidden bg-[var(--panel)] max-h-80 overflow-y-auto divide-y divide-[var(--border)]">
                  {!selectedJob.results || selectedJob.results.length === 0 ? (
                    <div className="p-4 text-center text-2xs text-[var(--text-muted)]">
                      No target devices recorded for this job.
                    </div>
                  ) : (
                    selectedJob.results
                      .filter((r) =>
                        r.hostname.toLowerCase().includes(deviceResultSearch.toLowerCase())
                      )
                      .map((res) => {
                        const isSelected = selectedResult?.deviceId === res.deviceId;
                        return (
                          <div
                            key={res.deviceId}
                            onClick={() => setSelectedResult(res)}
                            className={cn(
                              'p-3 cursor-pointer transition-colors flex items-center justify-between text-xs',
                              isSelected
                                ? 'bg-[var(--elevated)] border-l-2 border-l-[var(--accent)] font-semibold'
                                : 'hover:bg-[var(--hover)]'
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Laptop className="h-3.5 w-3.5 text-[var(--text-muted)] shrink-0" />
                              <span className="truncate text-[var(--text-primary)]">{res.hostname}</span>
                            </div>
                            <Badge variant={res.status}>{res.status}</Badge>
                          </div>
                        );
                      })
                  )}
                </div>

                {/* Right Execution Log Output Column */}
                <div className="md:col-span-2 border border-[var(--border)] rounded-md bg-[var(--panel)] p-4 flex flex-col h-80">
                  {selectedResult ? (
                    <div className="flex flex-col h-full space-y-2">
                      <div className="flex items-center justify-between border-b border-[var(--border)] pb-2 shrink-0">
                        <div>
                          <div className="text-xs font-semibold text-[var(--text-primary)] flex items-center gap-2">
                            <span>{selectedResult.hostname}</span>
                            <Badge variant={selectedResult.status}>{selectedResult.status}</Badge>
                          </div>
                          <div className="text-2xs text-[var(--text-muted)] mt-0.5">
                            Duration: {formatDuration(selectedResult.duration || 0)} • Exit Code:{' '}
                            {selectedResult.exitCode ?? 'N/A'}
                          </div>
                        </div>

                        {selectedResult.output && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCopyOutput(selectedResult.output)}
                            className="h-7 px-2 text-2xs"
                          >
                            {copiedLog ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                            {copiedLog ? 'Copied' : 'Copy Log'}
                          </Button>
                        )}
                      </div>

                      {/* Terminal Log Console View */}
                      <div className="flex-1 bg-[#050507] border border-[var(--border)] rounded p-3 overflow-y-auto font-mono text-2xs text-emerald-400 leading-relaxed">
                        {selectedResult.output ? (
                          <pre className="whitespace-pre-wrap font-mono">{selectedResult.output}</pre>
                        ) : selectedResult.error ? (
                          <pre className="whitespace-pre-wrap font-mono text-rose-400">
                            {selectedResult.error}
                          </pre>
                        ) : (
                          <span className="text-[var(--text-muted)] font-sans italic">
                            No console output available for this target execution.
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-xs text-[var(--text-muted)]">
                      <Terminal className="h-8 w-8 text-[var(--text-muted)] mb-2" />
                      Select a device on the left to inspect its output log.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm Job Cancellation Modal Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Running Job"
        message={
          <span>
            Are you sure you want to cancel execution for{' '}
            <strong className="text-[var(--text-primary)]">{jobToCancel?.name}</strong>? Any active device
            agents running this script will be sent an abort signal.
          </span>
        }
        confirmLabel="Abort Job Execution"
        danger
      />
    </div>
  );
}
